//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import {
  ILike2BuyItemsSearchRequest,
  ILike2BuyItemsSearchResult,
  ILike2BuyItemsSearchResponse,
  ILike2BuyItemsRequest,
  IAddLike2BuyItemsResponse,
  IGetPinnedLike2BuyItemsResponse,
  IGetLike2BuyItemsByCuidResponse,
} from '@apis/like2buy/like2buyModerationApiModel';
import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function like2buyModerationFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'instagram_dashboard_gateway',
    `/api/v1/like2buy/moderation${endpoint}`,
    init,
  );
}

/**
 *
 * Searches for Like2Buy items for the current client
 *
 * GET /api/v1/like2buy/moderation/items
 */
export async function getLike2BuyItems(
  req: ILike2BuyItemsSearchRequest,
): Promise<ILike2BuyItemsSearchResult> {
  const params = makeParams(req);
  const res = await like2buyModerationFetch(`/items?${params}`);
  const like2buyItemsReponse = await res.json<ILike2BuyItemsSearchResponse>(
    (res) => ({
      data: {
        items: res.data.items.map(deserializeMediaItem),
        totalCount: res.data.totalCount,
      },
    }),
  );

  return like2buyItemsReponse.data;
}

/**
 *
 * Adds items to the current client's Like2Buy Moderation
 *
 * POST /api/v1/like2buy/moderation/items
 */
export async function addLike2BuyItems(
  req: ILike2BuyItemsRequest,
): Promise<{ alreadyAddedItems: IMlsMediaItem[] }> {
  const res = await like2buyModerationFetch(`/items`, {
    method: 'POST',
    body: JSON.stringify(req),
  });

  const addItemsReponse = await res.json<IAddLike2BuyItemsResponse>((res) => ({
    data: {
      alreadyAddedItems: res.data.alreadyAddedItems.map(deserializeMediaItem),
    },
  }));

  return addItemsReponse.data;
}

/**
 *
 * Removes items from the current client's Like2Buy Moderation
 *
 * POST /api/v1/like2buy/moderation/items/remove
 */
export async function removeLike2BuyItems(
  req: ILike2BuyItemsRequest,
): Promise<void> {
  await like2buyModerationFetch(`/items/remove`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/**
 *
 * Gets the current client's pinned Like2Buy items
 *
 * GET /api/v1/like2buy/moderation/items/pinned
 */
export async function getPinnedLike2BuyItems(): Promise<IMlsMediaItem[]> {
  const res = await like2buyModerationFetch(`/items/pinned`);
  const pinnedItemsResponse = await res.json<IGetPinnedLike2BuyItemsResponse>(
    (res) => ({
      data: res.data.map(deserializeMediaItem),
    }),
  );

  return pinnedItemsResponse.data;
}

/**
 *
 * Sets the current client's pinned Like2Buy items
 *
 * POST /api/v1/like2buy/moderation/items/pinned
 */
export async function setPinnedLike2BuyItems(
  req: ILike2BuyItemsRequest,
): Promise<void> {
  await like2buyModerationFetch(`/items/pinned`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/**
 *
 * Publishes items to the current client's Like2Buy Store
 *
 * POST /api/v1/like2buy/moderation/items/publish
 */
export async function publishLike2BuyItems(
  req: ILike2BuyItemsRequest,
): Promise<void> {
  await like2buyModerationFetch(`/items/publish`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/**
 *
 * Unpublishes items from the current client's Like2Buy Store, moving them to the 'Hidden' status.
 *
 * POST /api/v1/like2buy/moderation/items/unpublish
 */
export async function hideLike2BuyItems(
  req: ILike2BuyItemsRequest,
): Promise<void> {
  await like2buyModerationFetch(`/items/unpublish`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/**
 *
 * Gets the materialized Like2Buy items by cuid
 *
 * POST /api/v1/like2buy/moderation/items/materialize
 */
export async function getLike2BuyItemsByCuid(
  req: ILike2BuyItemsRequest,
): Promise<IMlsMediaItem[]> {
  const res = await like2buyModerationFetch(`/items/materialize`, {
    method: 'POST',
    body: JSON.stringify(req),
  });

  const itemsResponse = await res.json<IGetLike2BuyItemsByCuidResponse>(
    (res) => ({
      data: res.data.map(deserializeMediaItem),
    }),
  );

  return itemsResponse.data;
}
